<template>
  <div class="field-group fill-in-blank-answer-view option-list">
    <!-- Correct Answer -->
    <div class="field-group correct-answer">
      <dl class="field-set option-set" :class="{ grouped: isGrouped }" :style="groupStyle">
        <dd class="field-value">
          <!-- Icon -->
          <span class="icon">
            <svg-icon name="Checkmark16" kind="indicative" />
          </span>
          <!-- Answer / Group Label -->
          <div class="label">
            <div class="group-label">
              {{ labelComputed }}
            </div>
            <div v-if="memberSummary" class="group-summary">
              {{ memberSummary }}
            </div>
          </div>
          <!-- Case Sensitive -->
          <cv-interactive-tooltip
            v-if="showCaseSensitiveTooltip"
            class="field-tooltip case-sensitive"
          >
            <template #trigger>
              <svg-icon name="LetterCase24" kind="action" />
            </template>
            <template #content>
              <p>{{ t("question_component_label_case_sensitive") }}</p>
            </template>
          </cv-interactive-tooltip>
        </dd>
      </dl>

      <!-- Possible Answers -->
      <dl class="field-set answer-list">
        <dt class="field-label">
          <h5 class="title">
            {{ `${headerPrefix} - ${t("question_component_label_correct_answers")}` }}
          </h5>
          <cv-button
            v-if="isExpandable"
            class="view-all"
            kind="tertiary"
            size="small"
            type="button"
            @click.stop="handleViewAll"
            >{{ t("question_component_label_view_all") }}
            <span class="number">({{ answerList.length }})</span>
          </cv-button>
        </dt>
        <dd class="field-value">
          <div
            v-for="minimalAnswer in minimalAnswerList"
            :key="minimalAnswer"
            class="answer option"
          >
            {{ minimalAnswer }}
          </div>
        </dd>
      </dl>

      <!-- Answer Feedback -->
      <dl v-if="answer.feedback" class="field-set feedback">
        <dd class="field-value">
          <rich-text-card
            class="option-feedback"
            :title="t('question_option_title_feedback')"
            :content="answer.feedback"
            truncation-type="secondary"
          />
        </dd>
      </dl>
    </div>

    <!-- Distractor -->
    <div v-if="isDropdown" class="field-group distractors">
      <dl class="field-set option-set" :class="{ grouped: isGrouped }" :style="groupStyle">
        <dd class="field-value">
          <span class="icon">
            <svg-icon name="Cross16" kind="indicative" />
          </span>
          <div class="label">
            <div class="group-label">
              {{
                t("question_component_label_distractor_title", {
                  count: isGrouped ? props.label : index,
                })
              }}
            </div>
            <div v-if="memberSummary" class="group-summary">
              {{ memberSummary }}
            </div>
          </div>
        </dd>
      </dl>

      <!-- Possible Distractors -->
      <dl class="field-set distractor-list">
        <dt class="field-label">
          <h5 class="title">
            {{ `${headerPrefix} - ${t("question_component_label_distractors_title")}` }}
          </h5>
          <cv-button
            v-if="isExpandable"
            class="view-all"
            kind="tertiary"
            size="small"
            type="button"
            @click.stop="handleViewAllDistractors"
            >{{ t("question_component_label_view_all") }}
            <span class="number">({{ distractorList.length }})</span>
          </cv-button>
        </dt>
        <dd class="field-value">
          <div
            v-for="distractor in minimalDistractorList"
            :key="distractor"
            class="distractor option"
          >
            {{ distractor }}
          </div>
        </dd>
      </dl>

      <!-- Distractor Feedback -->
      <dl v-if="answer.wrongAnswerFeedback" class="field-set feedback">
        <dd class="field-value">
          <rich-text-card
            class="option-feedback"
            :title="t('question_option_title_feedback')"
            :content="answer.wrongAnswerFeedback"
            truncation-type="secondary"
          />
        </dd>
      </dl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { OPTION_DISPLAY_MODE } from "@modules/editor/service/editor.constant";
import TagListContentSubpage from "@plugins/common/components/TagListContentSubpage.vue";
import { cloneDeep } from "lodash";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import RichTextCard from "@plugins/richtext/components/RichTextCard.vue";

interface Props {
  answer: {
    answer: string;
    feedback?: string;
    caseSensitive?: boolean;
    wrongAnswer?: string;
    wrongAnswerFeedback?: string;
  };
  groupColor?: string | null;
  index: number;
  isGrouped?: boolean;
  label?: string;
  memberBlankNumbers?: number[];
  optionDisplayMode: string;
  showCaseSensitive?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  groupColor: null,
  isGrouped: false,
  label: "",
  memberBlankNumbers: () => [],
  showCaseSensitive: true,
});

const { t } = useI18n();
const contentSubpageInstance = useContentSubpageInstance();

// Refs
const answerList = ref<string[]>(props.answer.answer.split("|"));
const getInitMinimalAnswerList = () => {
  const minimalAnswerList = cloneDeep(answerList.value);
  minimalAnswerList.splice(2, answerList.value.length - 2);
  return minimalAnswerList;
};
const minimalAnswerList = ref<string[]>(getInitMinimalAnswerList());

// Computed
const isExpandable = computed(() => {
  return answerList.value.length > 0;
});

const isDropdown = computed(() => {
  return props.optionDisplayMode === OPTION_DISPLAY_MODE.DROPDOWN.value;
});

const showCaseSensitiveTooltip = computed(() => {
  return props.showCaseSensitive && props.answer.caseSensitive;
});

const labelComputed = computed(() => {
  if (props.label) {
    return t("question_component_label_answer_title", { count: props.label });
  }

  return t("question_component_label_blank_title", { count: props.index });
});

const headerPrefix = computed(() => {
  return props.isGrouped
    ? props.label
    : t("question_component_label_blank_title", { count: props.index });
});

const memberSummary = computed(() => {
  if (!props.isGrouped || props.memberBlankNumbers.length < 2) {
    return "";
  }

  return t("question_component_fitb_group_consists_of_blanks_label", {
    blanks: props.memberBlankNumbers.join(", "),
  });
});

const groupStyle = computed(() => {
  if (!props.isGrouped || !props.groupColor) {
    return undefined;
  }

  return {
    "--fitb-group-color": props.groupColor,
  };
});

const distractorList = computed(() => {
  return props.answer.wrongAnswer ? props.answer.wrongAnswer.split("|") : [];
});

const minimalDistractorList = computed(() => {
  return distractorList.value.slice(0, 2);
});

// Methods
const handleViewAll = () => {
  contentSubpageInstance.show(TagListContentSubpage, {
    title: t("question_component_label_correct_answers"),
    list: answerList.value,
    listTitle: t("question_component_label_fib_acceptable_selections"),
  });
};

const handleViewAllDistractors = () => {
  contentSubpageInstance.show(TagListContentSubpage, {
    title: t("question_component_label_distractors_title"),
    list: distractorList.value,
    listTitle: t("question_component_label_distractors_list_title"),
  });
};
</script>
